import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import ReactMapGL, { MapRef, ViewState } from "react-map-gl";
import { useTranslation } from "react-i18next";
import styles from "../../main/ObjectDetails/ObjectDetails.module.scss";
import AnimatedDetailsContainer from "../../../../share/ResultDetails/AnimatedDetailsContainer/AnimatedDetailsContainer";
import DetailsHeader from "../../../../share/ResultDetails/DetailsHeader/DetailsHeader";
import Details from "./ActiveTabs/Details";
import Contacts from "./ActiveTabs/Contacts";
import Objects from "./ActiveTabs/Objects";
import CustomMarker from "../../_mapComponents/CustomMarker/CustomMarker";
import { ICurrentMarker } from "../../../../models/CurrentMarker";
import { CurrentSikaDesigner } from "../../../../models/CurrentSikaDesigner";
import {
  defaultViewport,
  mapboxAccessToken,
} from "../../../../constants/mapConstants";
import util from "../../../../util";
import useSelectedLanguage from "../../../../hooks/useSelectedLanguage";
import Jp from "../../../../assets/svg/jpIcon.svg";

interface ListViewSikaDesignerDetailsProps {
  designer: CurrentSikaDesigner;
  isDetailsActive: boolean;
  onClose: () => void;
}

const SIKA_MAP_STYLE = "mapbox://styles/aimap/cle6wbqd2005h01qm41h8z5xb";

const ListViewSikaDesignerDetails: React.FC<
  ListViewSikaDesignerDetailsProps
> = ({ designer, isDetailsActive, onClose }) => {
  const selectedLanguage = useSelectedLanguage();
  const { t: tObjectDetails } = useTranslation("translation", {
    keyPrefix: "mainPage.mapFilters.objectDetails",
  });

  const mapRef = useRef<MapRef | null>(null);
  const [viewState, setViewState] = useState<ViewState>(() => {
    const initialLat = Number(designer?.lat);
    const initialLon = Number(designer?.lon);
    const hasInitialCoordinates =
      Number.isFinite(initialLat) && Number.isFinite(initialLon);

    return {
      latitude: hasInitialCoordinates ? initialLat : defaultViewport.latitude,
      longitude: hasInitialCoordinates ? initialLon : defaultViewport.longitude,
      zoom: hasInitialCoordinates ? 13 : defaultViewport.zoom,
      bearing: 0,
      pitch: 0,
    };
  });

  useEffect(() => {
    const rawLat = Number(designer?.lat);
    const rawLon = Number(designer?.lon);
    const hasCoordinates = Number.isFinite(rawLat) && Number.isFinite(rawLon);

    setViewState((prev) => ({
      ...prev,
      latitude: hasCoordinates ? rawLat : defaultViewport.latitude,
      longitude: hasCoordinates ? rawLon : defaultViewport.longitude,
      zoom: hasCoordinates ? 13 : defaultViewport.zoom,
    }));
  }, [designer?.lat, designer?.lon]);

  const marker = useMemo<ICurrentMarker>(() => {
    const latitude = Number(designer?.lat);
    const longitude = Number(designer?.lon);

    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return { lat: latitude, lon: longitude };
    }

    return null;
  }, [designer?.lat, designer?.lon]);

  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    if (map) {
      util.mapInterfaceUtil.changeMapboxLanguage(map, selectedLanguage);
      util.mapInterfaceUtil.addPinIconToMapbox(map);
    }
  }, [selectedLanguage]);

  const description = designer?.address || "";
  const title = designer?.short_name || designer?.full_name || "";

  return (
    <AnimatedDetailsContainer isShown={isDetailsActive}>
      <DetailsHeader
        title={title}
        description={description}
        closeDetails={onClose}
        logo={<img src={Jp} alt="" />}
      />
      <div className={styles.listViewDetailsLayout}>
        <div className={styles.listViewMapWrapper}>
          <ReactMapGL
            {...viewState}
            ref={(instance) => (mapRef.current = instance)}
            mapboxAccessToken={mapboxAccessToken}
            mapStyle={SIKA_MAP_STYLE}
            onMove={(event) => setViewState(event.viewState)}
            dragRotate={false}
            doubleClickZoom={false}
            scrollZoom={false}
            attributionControl={false}
            style={{ width: "100%", height: "100%" }}
            onLoad={handleMapLoad}
          >
            {marker && <CustomMarker marker={marker} />}
          </ReactMapGL>
        </div>
        <div className={styles.listViewPanels}>
          <section className={styles.listViewPanel}>
            <h3 className={styles.listViewPanelTitle}>
              {tObjectDetails("details.title")}
            </h3>
            <Details
              key={`details-${designer?.edrpou_code ?? designer?.full_name}`}
              contentClassName={styles.listViewPanelContent}
            />
          </section>
          <section className={styles.listViewPanel}>
            <h3 className={styles.listViewPanelTitle}>
              {tObjectDetails("contacts.title")}
            </h3>
            <Contacts
              key={`contacts-${designer?.edrpou_code ?? designer?.full_name}`}
              contentClassName={styles.listViewPanelContent}
            />
          </section>
          <section
            className={classNames(
              styles.listViewPanel,
              styles.listViewPanelWide
            )}
          >
            <h3 className={styles.listViewPanelTitle}>
              {tObjectDetails("projects.title")}
            </h3>
            <Objects
              key={`objects-${designer?.edrpou_code ?? designer?.full_name}`}
              contentClassName={classNames(
                styles.listViewPanelContent,
                styles.listViewProjectsContent
              )}
            />
          </section>
        </div>
      </div>
    </AnimatedDetailsContainer>
  );
};

export default ListViewSikaDesignerDetails;
